package one.healthium.decompose

import androidx.compose.runtime.Immutable
import com.arkivanov.decompose.Child
import com.arkivanov.decompose.ComponentContext
import com.arkivanov.decompose.router.children.ChildNavState
import com.arkivanov.decompose.router.children.NavState
import com.arkivanov.decompose.router.children.NavigationSource
import com.arkivanov.decompose.router.children.SimpleChildNavState
import com.arkivanov.decompose.router.children.SimpleNavigation
import com.arkivanov.decompose.router.children.children
import com.arkivanov.decompose.value.Value
import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.Transient

fun <C : Any> SimpleNavigation<ModalStackNavEvent<C>>.push(config: C) {
    navigate { it + ModalC(config) }
}

fun <C : Any> SimpleNavigation<ModalStackNavEvent<C>>.dismiss(block: (C) -> Boolean) {
    navigate {
        it.map { wrapper ->
            if (block(wrapper.config)) wrapper.copy(toDismiss = true)
            else wrapper
        }
    }
}

fun <C : Any> SimpleNavigation<ModalStackNavEvent<C>>.pop(config: Any) {
    navigate { it.filter { wrapper -> wrapper != config } }
}

data class ModalStack<out C : Any, out T : Any>(
    val items: List<Child.Created<C, T>>,
)

typealias ModalStackNavEvent<C> = (List<ModalC<C>>) -> List<ModalC<C>>

@Suppress("UNCHECKED_CAST")
fun <C : Any, T : Any> ComponentContext.modalStack(
    source: NavigationSource<ModalStackNavEvent<C>>,
    serializer: KSerializer<C>,
    initialStack: () -> List<C> = ::emptyList,
    key: String = "ModalStack",
    childFactory: (configuration: C, componentContext: ComponentContext) -> T,
): Value<ModalStack<ModalC<C>, ModalT<T>>> =
    children(
        source = source,
        stateSerializer = ModalStackNavState.serializer(serializer),
        initialState = { ModalStackNavState(initialStack().map { ModalC(it) }) },
        key = key,
        navTransformer = { state, event -> ModalStackNavState(event(state.items)) },
        stateMapper = { _, children -> ModalStack(children as List<Child.Created<ModalC<C>, ModalT<T>>>) },
        backTransformer = { null },
        childFactory = { modal, componentContext ->
            val child = childFactory(modal.config, componentContext)
            ModalT(child, modal.toDismiss)
        },
    )

@Serializable
internal data class ModalStackNavState<C : Any>(
    val items: List<ModalC<C>>,
) : NavState<ModalC<C>> {
    @Transient
    override val children: List<ChildNavState<ModalC<C>>> =
        items.mapIndexed { index, modalChild ->
            SimpleChildNavState(
                configuration = modalChild,
                status = if (index == items.lastIndex) {
                    ChildNavState.Status.RESUMED
                } else {
                    ChildNavState.Status.CREATED
                },
            )
        }
}

@Serializable
data class ModalC<C>(
    val config: C,
    val toDismiss: Boolean = false,
)

@Immutable
class ModalT<T>(
    val child: T,
    val toDismiss: Boolean,
)